import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";

import {
  Add,
  DeleteOutline,
  EditOutlined,
  PauseCircleOutline,
  PlayCircleOutline,
  Refresh,
  Search,
  VisibilityOutlined,
} from "@mui/icons-material";

import {
  useNavigate,
} from "react-router-dom";

import toast from "react-hot-toast";

import {
  advertisementsApi,
} from "../api/advertisementsApi";

import type {
  Advertisement,
  AdvertisementContentType,
  AdvertisementPlacement,
  AdvertisementStatus,
} from "../types/advertisement.types";

import {
  useAuth,
} from "../contexts/AuthContext";

import "../screenstyles/Advertisements.css";

const statusLabels:
Record<
  AdvertisementStatus,
  string
> = {
  draft: "مسودة",
  scheduled: "مجدول",
  active: "نشط",
  paused: "متوقف",
  expired: "منتهي",
  archived: "مؤرشف",
};

const contentLabels:
Record<
  AdvertisementContentType,
  string
> = {
  product: "منتج",
  service: "خدمة",
  category: "تصنيف",
  brand: "براند",
  url: "رابط",
  custom: "مخصص",
};

const placementLabels:
Record<
  AdvertisementPlacement,
  string
> = {
  home_top:
    "الرئيسية - أعلى",

  home_middle:
    "الرئيسية - وسط",

  products_top:
    "المنتجات - أعلى",

  products_middle:
    "المنتجات - وسط",

  product_details:
    "تفاصيل المنتج",

    service_details:
  "تفاصيل الخدمة",
  points:
    "النقاط",

  orders:
    "الطلبات",
};

function formatDate(
  value: string,
) {
  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      dateStyle:
        "medium",
      timeStyle:
        "short",
    },
  ).format(date);
}

function getStatusClass(
  status:
    AdvertisementStatus,
) {
  return `advertisements-status advertisements-status-${status}`;
}

const Advertisements:
React.FC = () => {
  const navigate =
    useNavigate();

  const {
    hasPermission,
  } = useAuth();

  const [
    items,
    setItems,
  ] =
    useState<
      Advertisement[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    status,
    setStatus,
  ] =
    useState<
      AdvertisementStatus |
      ""
    >("");

  const [
    contentType,
    setContentType,
  ] =
    useState<
      AdvertisementContentType |
      ""
    >("");

  const [
    placement,
    setPlacement,
  ] =
    useState<
      AdvertisementPlacement |
      ""
    >("");

  const [
    cursor,
    setCursor,
  ] =
    useState<
      string | undefined
    >();

  const [
    previousCursors,
    setPreviousCursors,
  ] =
    useState<
      (
        | string
        | undefined
      )[]
    >([]);

  const [
    nextCursor,
    setNextCursor,
  ] =
    useState<
      string | null
    >(null);

  const [
    hasMore,
    setHasMore,
  ] =
    useState(false);

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState<
      string | null
    >(null);

  const load =
    useCallback(
      async () => {
        try {
          setLoading(
            true,
          );

          const result =
            await advertisementsApi.list({
              limit: 30,

              ...(status
                ? {
                    status,
                  }
                : {}),

              ...(contentType
                ? {
                    contentType,
                  }
                : {}),

              ...(placement
                ? {
                    placement,
                  }
                : {}),

              ...(cursor
                ? {
                    cursor,
                  }
                : {}),
            });

          setItems(
            result.items,
          );

          setNextCursor(
            result.nextCursor,
          );

          setHasMore(
            result.hasMore,
          );
        } catch (
          error
        ) {
          console.error(
            error,
          );

          toast.error(
            "تعذر تحميل الإعلانات",
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        status,
        contentType,
        placement,
        cursor,
      ],
    );

  useEffect(() => {
    load();
  }, [
    load,
  ]);

  useEffect(() => {
    setCursor(
      undefined,
    );

    setPreviousCursors(
      [],
    );
  }, [
    status,
    contentType,
    placement,
  ]);

  const visibleItems =
    useMemo(
      () => {
        const value =
          search
            .trim()
            .toLowerCase();

        if (!value) {
          return items;
        }

        return items.filter(
          advertisement => {
            const titles =
              Object.values(
                advertisement
                  .translations,
              )
                .map(
                  translation =>
                    translation
                      ?.title ??
                    "",
                )
                .join(" ")
                .toLowerCase();

            return (
              advertisement.name
                .toLowerCase()
                .includes(
                  value,
                ) ||
              titles.includes(
                value,
              )
            );
          },
        );
      },
      [
        items,
        search,
      ],
    );

  if (
    !hasPermission(
      "advertisements.view",
    )
  ) {
    return (
      <div className="advertisement-access-denied">
        <div className="advertisement-access-denied-card">
          <h2 className="advertisement-access-denied-title">
            لا تملك صلاحية عرض الإعلانات.
          </h2>
        </div>
      </div>
    );
  }

  const performAction =
    async (
      id: string,
      action:
        | "publish"
        | "pause"
        | "archive",
    ) => {
      try {
        if (
          action ===
          "archive"
        ) {
          const confirmed =
            window.confirm(
              "هل أنت متأكد من أرشفة هذا الإعلان؟",
            );

          if (
            !confirmed
          ) {
            return;
          }
        }

        setActionLoading(
          id,
        );

        if (
          action ===
          "publish"
        ) {
          await advertisementsApi.publish(
            id,
          );

          toast.success(
            "تم نشر الإعلان",
          );
        }

        if (
          action ===
          "pause"
        ) {
          await advertisementsApi.pause(
            id,
          );

          toast.success(
            "تم إيقاف الإعلان",
          );
        }

        if (
          action ===
          "archive"
        ) {
          await advertisementsApi.archive(
            id,
          );

          toast.success(
            "تمت أرشفة الإعلان",
          );
        }

        await load();
      } catch (
        error
      ) {
        console.error(
          error,
        );

        toast.error(
          "تعذر تنفيذ العملية",
        );
      } finally {
        setActionLoading(
          null,
        );
      }
    };

  return (
    <main
      className="advertisement-page advertisements-list-page"
      dir="rtl"
    >
      <header className="advertisement-page-header advertisements-list-header">
        <div className="advertisement-page-header-content">
          <span className="advertisement-page-eyebrow">
            إدارة المحتوى
          </span>

          <h1 className="advertisement-page-title">
            إدارة الإعلانات
          </h1>

          <p className="advertisement-page-description">
            إدارة إعلانات تطبيق شوف،
            والتحكم بالاستهداف
            والجدولة والأولوية
            وحالة النشر من مكان واحد.
          </p>

          <div className="advertisement-page-meta">
            <span className="advertisement-page-meta-chip">
              عدد النتائج:{" "}
              {visibleItems.length}
            </span>

            <span className="advertisement-page-meta-chip">
              الصفحة الحالية:{" "}
              {previousCursors.length +
                1}
            </span>
          </div>
        </div>

        <div className="advertisement-page-header-actions">
          <Tooltip title="تحديث">
            <button
              type="button"
              className="advertisements-header-icon-btn"
              onClick={
                load
              }
            >
              <Refresh />
            </button>
          </Tooltip>

          {hasPermission(
            "advertisements.create",
          ) && (
            <button
              type="button"
              className="advertisements-add-btn"
              onClick={() =>
                navigate(
                  "/advertisements/add",
                )
              }
            >
              <Add />

              <span>
                إضافة إعلان
              </span>
            </button>
          )}
        </div>
      </header>

      <section className="advertisements-filters-card">
        <div className="advertisements-search-box">
          <Search className="advertisements-search-icon" />

          <input
            type="text"
            className="advertisements-search-input"
            placeholder="البحث باسم الإعلان أو عنوانه..."
            value={
              search
            }
            onChange={
              event =>
                setSearch(
                  event.target
                    .value,
                )
            }
          />
        </div>

        <div className="advertisements-filter-item">
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>
              الحالة
            </InputLabel>

            <Select
              label="الحالة"
              value={
                status
              }
              onChange={
                event =>
                  setStatus(
                    event
                      .target
                      .value as
                      | AdvertisementStatus
                      | "",
                  )
              }
            >
              <MenuItem value="">
                جميع الحالات
              </MenuItem>

              {Object.entries(
                statusLabels,
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <MenuItem
                    key={
                      value
                    }
                    value={
                      value
                    }
                  >
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </div>

        <div className="advertisements-filter-item">
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>
              نوع المحتوى
            </InputLabel>

            <Select
              label="نوع المحتوى"
              value={
                contentType
              }
              onChange={
                event =>
                  setContentType(
                    event
                      .target
                      .value as
                      | AdvertisementContentType
                      | "",
                  )
              }
            >
              <MenuItem value="">
                جميع الأنواع
              </MenuItem>

              {Object.entries(
                contentLabels,
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <MenuItem
                    key={
                      value
                    }
                    value={
                      value
                    }
                  >
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </div>

        <div className="advertisements-filter-item">
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>
              مكان الظهور
            </InputLabel>

            <Select
              label="مكان الظهور"
              value={
                placement
              }
              onChange={
                event =>
                  setPlacement(
                    event
                      .target
                      .value as
                      | AdvertisementPlacement
                      | "",
                  )
              }
            >
              <MenuItem value="">
                جميع الأماكن
              </MenuItem>

              {Object.entries(
                placementLabels,
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <MenuItem
                    key={
                      value
                    }
                    value={
                      value
                    }
                  >
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </div>
      </section>

      <section className="advertisements-table-card">
        {loading ? (
          <div className="advertisements-loading">
            <CircularProgress />

            <span>
              جارٍ تحميل الإعلانات...
            </span>
          </div>
        ) : visibleItems.length ===
          0 ? (
          <div className="advertisements-empty">
            لا توجد إعلانات مطابقة.
          </div>
        ) : (
          <div className="advertisements-table-wrapper">
            <table className="advertisements-table">
              <thead>
                <tr>
                  <th>
                    الإعلان
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    النوع
                  </th>

                  <th>
                    الاستهداف
                  </th>

                  <th>
                    البداية
                  </th>

                  <th>
                    النهاية
                  </th>

                  <th>
                    الأولوية
                  </th>

                  <th>
                    الإجراءات
                  </th>
                </tr>
              </thead>

              <tbody>
                {visibleItems.map(
                  advertisement => {
                    const title =
                      advertisement
                        .translations
                        .ar
                        ?.title ??
                      advertisement
                        .translations
                        .en
                        ?.title ??
                      advertisement
                        .translations
                        .ku
                        ?.title ??
                      "بدون عنوان";

                    const image =
                      advertisement
                        .media
                        .mobileImageUrl ??
                      advertisement
                        .media
                        .imageUrl;

                    const isBusy =
                      actionLoading ===
                      advertisement.id;

                    return (
                      <tr
                        key={
                          advertisement.id
                        }
                      >
                        <td>
                          <div className="advertisements-ad-cell">
                           {image ? (
  <img
    className="advertisements-ad-image"
    src={
      image
    }
    alt={
      advertisement.name
    }
  />
) : (
  <div className="advertisements-ad-image-placeholder">
    بدون صورة
  </div>
)}

                            <div className="advertisements-ad-info">
                              <strong>
                                {
                                  advertisement.name
                                }
                              </strong>

                              <span>
                                {
                                  title
                                }
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={getStatusClass(
                              advertisement.status,
                            )}
                          >
                            {
                              statusLabels[
                                advertisement
                                  .status
                              ]
                            }
                          </span>
                        </td>

                        <td>
                          <span className="advertisements-content-badge">
                            {
                              contentLabels[
                                advertisement
                                  .contentType
                              ]
                            }
                          </span>
                        </td>

                        <td>
                          <span className="advertisements-targeting-text">
                            {advertisement
                              .targeting
                              .accountTypes
                              .length >
                            0
                              ? advertisement
                                  .targeting
                                  .accountTypes
                                  .join(
                                    "، ",
                                  )
                              : "الجميع"}
                          </span>
                        </td>

                        <td>
                          <span className="advertisements-date">
                            {formatDate(
                              advertisement.startAt,
                            )}
                          </span>
                        </td>

                        <td>
                          <span className="advertisements-date">
                            {formatDate(
                              advertisement.endAt,
                            )}
                          </span>
                        </td>

                        <td>
                          <span className="advertisements-priority">
                            {
                              advertisement.priority
                            }
                          </span>
                        </td>

                        <td>
                          <div className="advertisements-actions">
                            <Tooltip title="عرض">
                              <button
                                type="button"
                                className="advertisements-action-btn advertisements-view-btn"
                                onClick={() =>
                                  navigate(
                                    `/advertisements/${advertisement.id}`,
                                  )
                                }
                              >
                                <VisibilityOutlined />
                              </button>
                            </Tooltip>

                            {hasPermission(
                              "advertisements.update",
                            ) && (
                              <Tooltip title="تعديل">
                                <button
                                  type="button"
                                  className="advertisements-action-btn advertisements-edit-btn"
                                  onClick={() =>
                                    navigate(
                                      `/advertisements/${advertisement.id}/edit`,
                                    )
                                  }
                                >
                                  <EditOutlined />
                                </button>
                              </Tooltip>
                            )}

                            {hasPermission(
                              "advertisements.publish",
                            ) &&
                              advertisement.status !==
                                "active" &&
                              advertisement.status !==
                                "archived" && (
                                <Tooltip title="نشر">
                                  <span>
                                    <button
                                      type="button"
                                      className="advertisements-action-btn advertisements-publish-btn"
                                      disabled={
                                        isBusy
                                      }
                                      onClick={() =>
                                        performAction(
                                          advertisement.id,
                                          "publish",
                                        )
                                      }
                                    >
                                      <PlayCircleOutline />
                                    </button>
                                  </span>
                                </Tooltip>
                              )}

                            {hasPermission(
                              "advertisements.pause",
                            ) &&
                              advertisement.status ===
                                "active" && (
                                <Tooltip title="إيقاف">
                                  <span>
                                    <button
                                      type="button"
                                      className="advertisements-action-btn advertisements-pause-btn"
                                      disabled={
                                        isBusy
                                      }
                                      onClick={() =>
                                        performAction(
                                          advertisement.id,
                                          "pause",
                                        )
                                      }
                                    >
                                      <PauseCircleOutline />
                                    </button>
                                  </span>
                                </Tooltip>
                              )}

                            {hasPermission(
                              "advertisements.delete",
                            ) &&
                              advertisement.status !==
                                "archived" && (
                                <Tooltip title="أرشفة">
                                  <span>
                                    <button
                                      type="button"
                                      className="advertisements-action-btn advertisements-delete-btn"
                                      disabled={
                                        isBusy
                                      }
                                      onClick={() =>
                                        performAction(
                                          advertisement.id,
                                          "archive",
                                        )
                                      }
                                    >
                                      <DeleteOutline />
                                    </button>
                                  </span>
                                </Tooltip>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="advertisements-pagination">
        <button
          type="button"
          className="advertisements-pagination-btn"
          disabled={
            previousCursors.length ===
            0
          }
          onClick={() => {
            const history = [
              ...previousCursors,
            ];

            const previous =
              history.pop();

            setPreviousCursors(
              history,
            );

            setCursor(
              previous,
            );
          }}
        >
          السابق
        </button>

        <span className="advertisements-page-number">
          الصفحة{" "}
          {previousCursors.length +
            1}
        </span>

        <button
          type="button"
          className="advertisements-pagination-btn"
          disabled={
            !hasMore ||
            !nextCursor
          }
          onClick={() => {
            setPreviousCursors(
              current => [
                ...current,
                cursor,
              ],
            );

            setCursor(
              nextCursor ??
              undefined,
            );
          }}
        >
          التالي
        </button>
      </footer>
    </main>
  );
};

export default Advertisements;